import React from 'react';
import { object } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';

import { useConfiguration } from '../../context/configurationContext';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import { getListingsById } from '../../ducks/marketplaceData.duck';
import { selectListingCollection } from '../../ducks/listingCollections.duck';
import {
  cantonBySlug,
  neighboursOf,
  regionSlug,
  regionSearchString,
  REGION_FIELD_KEY,
} from '../../util/regions';
import { regionPageSchema } from '../../util/landingSchema';
import { hasCategoryRegionPage } from '../../util/categoryRegions';

import { NamedLink } from '../../components';
import NotFoundPage from '../NotFoundPage/NotFoundPage';
import LandingLayout from './LandingLayout';
import {
  CATEGORIES,
  STEPS,
  SHARED_REGION_FAQ,
  defaultHeading,
  defaultIntro,
  defaultMetaDescription,
  defaultPageTitle,
} from './landingContent';
import regionsData from './data/regions.json';

import Hero from './sections/Hero';
import SectionHead from './sections/SectionHead';
import ClaimBanner from './sections/ClaimBanner';
import Usps from './sections/Usps';
import ListingGrid from './sections/ListingGrid';
import CardGrid from './sections/CardGrid';
import Steps from './sections/Steps';
import Faq from './sections/Faq';
import TrustBanner from './sections/TrustBanner';
import Testimonials from './sections/Testimonials';
import FinalCta from './sections/FinalCta';

/**
 * Copy for a canton, falling back field by field. A canton with no entry still
 * gets a complete page; a canton with copy overrides each field individually.
 */
const copyFor = option => regionsData.cantons?.[option] || {};

/** Canton-specific FAQ entries first, then the ones that hold everywhere. */
const faqItems = copy =>
  [
    { q: copy.faq1q, a: copy.faq1a },
    { q: copy.faq2q, a: copy.faq2a },
  ]
    .filter(entry => entry.q?.trim() && entry.a?.trim())
    .concat(SHARED_REGION_FAQ);

/**
 * What a canton with no suppliers shows instead of an empty grid.
 *
 * A page that simply renders nothing is the thin-content outcome the whole
 * design avoids, so the empty state carries real outgoing links.
 */
const EmptyState = ({ canton }) => {
  const neighbours = neighboursOf(canton.option, 4);
  return (
    <>
      <p className="listings-empty">
        {`Für ${canton.label} sind aktuell noch keine Anbieter:innen eingetragen. Viele Dienstleister:innen arbeiten kantonsübergreifend und reisen an.`}
      </p>
      {neighbours.length ? (
        <p className="listings-empty">
          Schaut in den angrenzenden Regionen:{' '}
          {neighbours.map((n, i) => (
            <React.Fragment key={n.option}>
              {i > 0 ? ', ' : ''}
              <NamedLink name="RegionLandingPage" params={{ slug: regionSlug(n.option) }}>
                {n.label}
              </NamedLink>
            </React.Fragment>
          ))}
          .
        </p>
      ) : null}
      <div style={{ textAlign: 'center', marginTop: '40px' }}>
        <NamedLink className="btn btn-ghost" name="SearchPage">
          Alle Angebote ansehen
        </NamedLink>
      </div>
    </>
  );
};

export const RegionLandingPageComponent = props => {
  const { params, listings, scrollingDisabled } = props;
  const config = useConfiguration();
  const canton = cantonBySlug(params?.slug);

  if (!canton) {
    return <NotFoundPage staticContext={props.staticContext} />;
  }

  const copy = copyFor(canton.option);
  const heading = copy.h1 || defaultHeading(canton);
  const intro = copy.intro || defaultIntro(canton);
  const description = copy.metaDescription || defaultMetaDescription(canton);
  const title = copy.seoTitle || defaultPageTitle(canton);

  // searchMode comes from the live field config so the CTA lands a visitor on
  // the same filter state the site's own Region filter would set.
  //
  // The indexForSearch check has to be here too, not only in the duck. The
  // search page drops params for unindexed fields, so a "Alle Anbieter:innen in
  // Uri ansehen" button would silently deliver UNFILTERED results - the same
  // wrong-content-is-worse-than-none failure the duck's guard exists to
  // prevent, just moved from the grid to the CTA. When the field is not
  // indexed the CTAs drop the region promise instead.
  const field = (config?.listing?.listingFields || []).find(f => f.key === REGION_FIELD_KEY);
  const isRegionSearchable = field?.filterConfig?.indexForSearch === true;
  const searchLink = isRegionSearchable
    ? {
        name: 'SearchPage',
        to: { search: regionSearchString(canton.option, field.filterConfig.searchMode) },
      }
    : { name: 'SearchPage' };
  const searchLabel = isRegionSearchable
    ? `Alle Anbieter:innen in ${canton.label} ansehen`
    : 'Alle Angebote ansehen';

  const faq = faqItems(copy);
  const neighbours = neighboursOf(canton.option, 6);

  return (
    <LandingLayout
      title={title}
      description={description}
      schema={regionPageSchema(canton, faq)}
      currentPage="RegionLandingPage"
      scrollingDisabled={scrollingDisabled}
    >
      <Hero
        crumbs={[
          { label: 'Startseite', link: { name: 'LandingPage' } },
          { label: 'Regionen', link: { name: 'RegionHubPage' } },
          { label: canton.label },
        ]}
        heading={heading}
        lead={intro}
        tagline={copy.heroTagline}
        primaryCta={{ link: searchLink, label: searchLabel }}
        secondaryCta={{ link: { href: '#faq' }, label: 'Häufige Fragen' }}
      />

      {copy.localIntro ? (
        <section aria-labelledby="local-title">
          <div className="container">
            <SectionHead
              id="local-title"
              heading={`Hochzeiten im Kanton ${canton.label}`}
              text={copy.localIntro}
            />
          </div>
        </section>
      ) : null}

      <ClaimBanner />
      <Usps />

      <ListingGrid
        listings={listings}
        heading={`Anbieter:innen in ${canton.label}`}
        text="Alle Dienstleister:innen, die diesen Kanton als Einsatzgebiet angeben."
        emptyState={<EmptyState canton={canton} />}
        footerLink={{ link: searchLink, label: searchLabel }}
      />

      <CardGrid
        id="cats-title"
        heading="Alle Kategorien"
        text="Alles, was eure Hochzeit braucht – an einem Ort."
        alt
        items={CATEGORIES.map(cat => ({
          key: cat.slug,
          link: hasCategoryRegionPage(cat.slug, canton.option)
            ? { name: 'CategoryRegionLandingPage', params: { slug: cat.slug, region: params.slug } }
            : { name: 'CategoryLandingPage', params: { slug: cat.slug } },
          label: cat.label,
          image: cat.image,
        }))}
      />

      <Steps heading={STEPS.heading} text={STEPS.text} items={STEPS.items} />
      <Faq heading="Häufige Fragen" items={faq} />

      <CardGrid
        id="neighbours-title"
        heading="Angrenzende Regionen"
        text="Viele Anbieter:innen arbeiten kantonsübergreifend."
        items={neighbours.map(n => ({
          key: n.option,
          link: { name: 'RegionLandingPage', params: { slug: regionSlug(n.option) } },
          label: n.label,
        }))}
        footerLink={{ link: { name: 'RegionHubPage' }, label: 'Alle Regionen ansehen' }}
      />

      <TrustBanner />
      <Testimonials />

      <FinalCta
        heading={`Findet eure Anbieter:innen in ${canton.label}`}
        text="Vergleicht Angebote, klärt offene Fragen und bucht direkt über die Plattform."
        link={searchLink}
        label="Jetzt Angebote entdecken"
      />
    </LandingLayout>
  );
};

RegionLandingPageComponent.propTypes = { params: object };

const mapStateToProps = (state, ownProps) => {
  const slug = ownProps?.params?.slug;
  const { listingIds } = selectListingCollection(state, `region-${slug}`);
  return {
    listings: getListingsById(state, listingIds),
    scrollingDisabled: isScrollingDisabled(state),
  };
};

export default compose(connect(mapStateToProps))(RegionLandingPageComponent);
